import {Component, inject, Input, OnDestroy, OnChanges} from '@angular/core';
import {HttpClient, HttpContext} from "@angular/common/http";
import {Subject, Subscription} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {BaseService} from "../../services/base.service";
import {SKIP_LOADER} from "../../services/loader.service";
import {DeliveryEstimate} from "../../interfaces/delivery-estimate";

// Скільки місць займе замовлення і скільки приблизно коштуватиме доставка
// Новою Поштою (картка #218). Кошик і оформлення замовлення показують один і той самий блок.
//
// Обидві цифри рахує бекенд (basket_data/delivery_estimate): місця - тим самим рушієм,
// який потім порахує групу замовлень, ціну дає сама Нова Пошта. Фронт нічого не
// довираховує: якщо показувати нема чого (у позиції немає габаритів, доставка не НП,
// замовлення поїде палетою, показ вимкнено в налаштуваннях), сервер віддає show: false -
// і блок не малюється зовсім. Помилитися в цифрі при клієнті дорожче, ніж її не показати.
//
// У кошику міста одержувача ще немає, тож приїдуть лише місця (delivery_cost = null) -
// ціна з'являється на етапі оформлення, коли обрано місто і відділення.
@Component({
  selector: 'app-delivery-estimate',
  template: `
    <div class="delivery-estimate" *ngIf="!compactPack && estimate && estimate.show">
      <b class="delivery-estimate__places">{{ placesText }}</b>
      <span class="delivery-estimate__cost" *ngIf="hasCost">{{ costText }}</span>
      <small class="delivery-estimate__hint" *ngIf="compactPackHint && estimate.places > 1">
        Кілька місць можна запакувати в один ящик - позначка «Запакувати максимально компактно» нижче.
      </small>
    </div>
  `,
  styles: [`
    .delivery-estimate {
      display: block;
      margin: 10px 0;
      padding: 8px 12px;
      border-left: 3px solid #FF8D33;
      background-color: #fbf6f1;
      font-family: 'Exo2Regular', sans-serif;
      font-size: 14px;
      line-height: 19px;
      color: #3b3b3b;
    }

    .delivery-estimate__places {
      font-family: 'Exo2SemiBold', sans-serif;
    }

    .delivery-estimate__cost {
      display: block;
    }

    .delivery-estimate__hint {
      display: block;
      margin-top: 3px;
      font-size: 12px;
      line-height: 16px;
      color: #7a7a7a;
    }
  `]
})
export class DeliveryEstimateComponent extends BaseService implements OnChanges, OnDestroy {
  // Обрана служба доставки. Рахуємо лише Нову Пошту - решту сервер відсіє сам.
  @Input() deliveryId: number;
  // UUID міста одержувача в НП. Порожній - приїдуть лише місця, без ціни.
  @Input() cityRef = '';
  // true - адресна доставка, false - у відділення.
  @Input() addressed = false;
  // Хто платить пересилку: 1 - за наш рахунок (зі знижкою), решта - одержувач.
  // Рахується тим самим правилом, що й при оформленні замовлення.
  @Input() shippingOnWho = 0;
  // Компактна XXL-упаковка: скільки місць вийде, вирішує склад, тож цифру ховаємо.
  @Input() compactPack = false;
  // Підказка про XXL-упаковку - лише там, де поряд є сама позначка (оформлення замовлення).
  @Input() compactPackHint = false;
  // Змінився кошик - привід перерахувати (кількість, видалення позиції тощо).
  @Input() refreshToken: any;
  // Рахувати фоном, без повноекранного лоадера. У кошику запит іде слідом за basket_data/data
  // і вдруге затемнював щойно показану сторінку (картка #388).
  @Input() background = false;

  estimate: DeliveryEstimate = null;

  private http = inject(HttpClient);
  private isDestroyed: Subject<boolean> = new Subject();
  private timer;
  private request: Subscription = null;

  ngOnChanges() {
    // Місто і відділення клієнт перемикає підряд - не сіпаємо сервер на кожну зміну.
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.load(), 300);
  }

  ngOnDestroy() {
    clearTimeout(this.timer);
    this.isDestroyed.next(true);
    this.isDestroyed.complete();
  }

  get hasCost(): boolean {
    return this.estimate && this.estimate.delivery_cost !== null && this.estimate.delivery_cost !== undefined;
  }

  // «Помістилося в одне місце» - окремим текстом: саме це клієнт і має побачити,
  // коли замовлення невелике (уточнення до картки).
  get placesText(): string {
    const places = this.estimate ? this.estimate.places : 0;

    if (places === 1) {
      return 'Замовлення поміститься в одне місце доставки';
    }

    return `Замовлення поїде у ${places} ${this.placesWord(places)} доставки`;
  }

  get costText(): string {
    if (this.estimate.delivery_cost === 0) {
      return 'Пересилку оплачуємо ми';
    }

    return `Орієнтовна вартість доставки Новою Поштою - ${this.estimate.delivery_cost} грн`;
  }

  private placesWord(places: number): string {
    const tens = places % 100;
    const ones = places % 10;

    if (tens >= 11 && tens <= 14) {
      return 'місць';
    }
    if (ones >= 2 && ones <= 4) {
      return 'місця';
    }

    return 'місць';
  }

  private load() {
    // Debounce лише відкладає старт: відповідь для попереднього міста, що прийшла пізніше,
    // перетирала розрахунок для поточного - попередній запит скасовуємо.
    if (this.request) {
      this.request.unsubscribe();
      this.request = null;
    }

    if (this.compactPack || !this.deliveryId) {
      this.estimate = null;
      return;
    }

    this.request = this.http.post(this.apiRoot + 'basket_data/delivery_estimate', {
      delivery_id: this.deliveryId,
      city_ref: this.cityRef || '',
      addressed: this.addressed,
      np_shipping_on_who: this.shippingOnWho,
    }, {
      context: new HttpContext().set(SKIP_LOADER, this.background)
    })
      .pipe(takeUntil(this.isDestroyed))
      .subscribe({
        next: (data: DeliveryEstimate) => {
          this.estimate = (data && data.show) ? data : null;
        },
        // Довідковий розрахунок не повинен ламати кошик і оформлення: не змогли - не показуємо.
        error: () => {
          this.estimate = null;
        }
      });
  }
}
